// Constants
import { SensitivityIconType } from "../constants/SensitivityConstants";
// Controls
import { Menu, MenuDivider, MenuItem, MenuItemRadio, MenuList, MenuPopover, MenuTrigger, mergeClasses } from "@fluentui/react-components";
import { CheckMark, Info } from "@ms/planner-generic-controls/lib/local/icons";
import { SensitivityIcon } from "./SensitivityIcon";
// Styles
import { sensitivityMenuPositioning, useSensitivityButtonMenuStyles } from "./SensitivityButtonMenu.styles";
// Utilities
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { safelyOpenExternalUrl } from "@ms/uno-utilities/lib/local/BrowserUtilities";
import * as React from "react";
/**
 * A menu component that displays sensitivity label options with icons and a learn more link.
 * Supports nested sublabels and highlights the currently selected label.
 *
 * @param props - The component props
 * @returns JSX element representing the sensitivity button menu
 */ export const SensitivityButtonMenu = ({ id, items, currentLabel, onApplyLabel, moreInfoUrl, checkedValues, onCheckedValueChange })=>{
    const classes = useSensitivityButtonMenuStyles();
    /**
     * Handler for the "Learn More" menu item click.
     * Opens the help URL in a new window if provided.
     */ const onLearnMoreClick = ()=>{
        if (moreInfoUrl) {
            safelyOpenExternalUrl(moreInfoUrl);
        }
    };
    return /*#__PURE__*/ React.createElement(MenuList, {
        id: id,
        className: classes.sensitivityMenuList,
        "data-testid": "sensitivity-menu-list"
    }, items.map((item)=>createMenuItemElement(item, currentLabel, onApplyLabel, classes, checkedValues, onCheckedValueChange)), moreInfoUrl && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(MenuDivider, {
        className: classes.menuDivider
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        icon: {
            className: classes.menuItemIcon,
            children: /*#__PURE__*/ React.createElement(Info, null)
        },
        content: {
            className: classes.menuItemLabel
        },
        onClick: onLearnMoreClick,
        checkmark: {
            className: classes.menuItemNoDefaultCheckmark
        },
        "data-testid": "sensitivity-menu-learn-more"
    }, Strings.ContentSensitivityLabel_MenuLearnMoreText)));
};
/**
 * A menu item component that displays a single sensitivity label option.
 * Shows a checkmark for the currently selected label and an icon indicating protection status.
 *
 * @param props - The component props
 * @returns JSX element representing a sensitivity label menu item
 */ const SensitivityButtonMenuItem = ({ item, currentLabel, onApplyLabel })=>{
    const classes = useSensitivityButtonMenuStyles();
    /**
     * Handler for when the menu item is clicked.
     */ const handleItemClick = ()=>{
        return onApplyLabel(item);
    };
    return /*#__PURE__*/ React.createElement(MenuItemRadio, {
        content: {
            className: classes.menuItemLabel
        },
        secondaryContent: currentLabel?.id === item.id ? /*#__PURE__*/ React.createElement(CheckMark, null) : undefined,
        icon: {
            className: classes.menuItemIcon,
            children: /*#__PURE__*/ React.createElement(SensitivityIcon, {
                iconType: item.hasProtection ? SensitivityIconType.TagProtected : SensitivityIconType.Tag,
                color: item.color
            })
        },
        onClick: handleItemClick,
        name: "label",
        value: item.id,
        checkmark: {
            className: classes.menuItemNoDefaultCheckmark
        },
        "data-testid": `sensitivity-menu-item-${item.id}`
    }, item.displayName);
};
/**
 * Creates a menu item element for a sensitivity label.
 * If the label has sublabels, creates a nested submenu; otherwise, creates a standard menu item.
 *
 * @param item - The sensitivity label item to create an element for
 * @param currentLabel - The currently selected sensitivity label
 * @param onApplyLabel - Callback function to apply a selected sensitivity label
 * @param classes - Style classes for the menu items
 * @returns JSX element representing the menu item (with or without submenu)
 */ const createMenuItemElement = (item, currentLabel, onApplyLabel, classes, checkedValues, onCheckedValueChange)=>{
    if (item.sublabels?.length) {
        return /*#__PURE__*/ React.createElement(Menu, {
            hasIcons: true,
            key: item.id,
            positioning: sensitivityMenuPositioning,
            checkedValues: checkedValues,
            onCheckedValueChange: onCheckedValueChange
        }, /*#__PURE__*/ React.createElement(MenuTrigger, {
            disableButtonEnhancement: true
        }, /*#__PURE__*/ React.createElement(MenuItem, {
            icon: {
                className: classes.menuItemIcon
            },
            content: {
                className: classes.menuItemLabel
            },
            checkmark: {
                className: classes.menuItemNoDefaultCheckmark
            },
            "data-testid": `sensitivity-menu-parent-${item.id}`
        }, item.displayName)), /*#__PURE__*/ React.createElement(MenuPopover, {
            className: mergeClasses(classes.tooltipContainer, classes.menuPopover),
            "data-testid": `sensitivity-submenu-${item.id}`
        }, /*#__PURE__*/ React.createElement(MenuList, {
            className: classes.sensitivityMenuList
        }, item.sublabels?.map((subItem)=>createMenuItemElement(subItem, currentLabel, onApplyLabel, classes)))));
    } else {
        return /*#__PURE__*/ React.createElement(SensitivityButtonMenuItem, {
            key: item.id,
            item: item,
            currentLabel: currentLabel,
            onApplyLabel: onApplyLabel
        });
    }
};
